import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import IconButton from '@material-ui/core/IconButton';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import { divide, isEmpty } from 'lodash';
import { NPointDetailLink, TestDetailLink } from '../links/Links';
import usePanelDataService from '../../services/PanelDataService';
import { LegacyIcon } from '../icon/Legacy';
import PerformanceMinMax, { TestLogLink } from '../charts/performance-min-max/PerformanceMinMax';
import SearchContainer from '../search/Search';
import useStyles from './PerformanceListStyles';
import { LoadingTextComponent, StatusTextComponent } from '../data-table/DataTable';
import { usePageStateValue } from '../../stores/PageStateStore';
import useIsPrinting, { getPrintRowOverride } from '../../utilities/PrintUtilities';
import useIsScheduledReport from '../../utilities/UseIsScheduledReport';
import { useId } from '../page-framework/panel/Panel';
import { Ng1DrillDownButton } from '../ng1-drill-down/Ng1DrillDownButton';

const PAGE_SIZE = 10;

const translatedPrevious = t`Previous Page`;
const translatedNext = t`Next Page`;

const Agent = ({ data }) => {
    const { agent } = data;
    const typeIcon = agent?.typeIcon;
    const classes = useStyles();

    return (
        <div className={classes.listItemTitle}>
            <span title={agent?.translatedType}>
                <LegacyIcon icon={typeIcon} fontSize="small" />
            </span>
            <NPointDetailLink nPoint={agent} />
        </div>
    );
};

const Test = ({ data }) => {
    const classes = useStyles();
    if (!data || !data.test) {
        return null;
    }

    const { test } = data;
    const typeIcon = test?.testType?.details?.icon;

    return (
        <div className={classes.listItemTitle}>
            <span title={test?.testType?.details?.title}>
                <LegacyIcon icon={typeIcon} fontSize="small" />
            </span>
            <TestDetailLink test={test} />
        </div>
    );
};

const AgentTestList = ({ data, classes, isTestPanel }) => {
    if (!data) {
        return (
            <div className={classes.dataMessageContainer}>
                <div className="dataMessageCenter">
                    <LoadingTextComponent text={<Trans>Loading Data</Trans>} />
                </div>
            </div>
        );
    }

    if (data?.data?.length === 0) {
        return (
            <div className={classes.dataMessageContainer}>
                <div className="dataMessageCenter">
                    <StatusTextComponent text={<Trans>No data available</Trans>} />
                </div>
            </div>
        );
    }

    const test = data?.entities?.test;
    if (isTestPanel) {
        return (
            <div className={classes.list}>
                {data?.data?.map((item) => (
                    <div className={clsx(classes.listItem, 'flex-container')} key={item?.agent?._id}>
                        <PerformanceMinMax
                            title={<Agent data={item} />}
                            test={test}
                            chartData={item}
                            tableData={item}
                            testLogLink={<TestLogLink showPath={test?.traceRoute?.enabled} agent={item?.agent?._id} />}
                            semLink={<Ng1DrillDownButton agent={item?.agent} test={test} />}
                        />
                    </div>
                ))}
            </div>
        );
    }

    const agent = data?.entities?.agent;
    return (
        <div className={classes.list}>
            {data?.data?.map((item) => (
                <div className={clsx(classes.listItem, 'flex-container')} key={item?.test?._id}>
                    <PerformanceMinMax
                        title={<Test data={item} />}
                        test={item.test}
                        chartData={item}
                        tableData={item}
                        testLogLink={<TestLogLink showPath={item?.test?.traceRoute?.enabled} test={item?.test?._id} />}
                        semLink={<Ng1DrillDownButton agent={agent} test={item?.test} />}
                    />
                </div>
            ))}
        </div>
    );
};

export default function PerformanceList({ dataSource, isTestPanel = false }) {
    const id = useId();
    const classes = useStyles();
    const isPrinting = useIsPrinting();
    const searchPageStateKey = `${id}-Search`;
    const [currentSearchState, searchStateActions] = usePageStateValue({
        key: searchPageStateKey,
        defaultValue: '',
    });
    const rowStartPageStateKey = `${id}-RowStart`;
    const [currentRowStartState, rowStartStateActions] = usePageStateValue({
        key: rowStartPageStateKey,
        defaultValue: 0,
    });

    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [pagingText, setPagingText] = useState('');
    const [allowPaging, setAllowPaging] = useState(false);
    const [allowPrevious, setAllowPrevious] = useState(false);
    const [allowNext, setAllowNext] = useState(false);

    const rowOverride = getPrintRowOverride();
    const isOverrideRowMode = isPrinting && rowOverride !== null;
    const isScheduledReport = useIsScheduledReport();

    const { rowStart, rowLimit } = useMemo(() => {
        if (isOverrideRowMode) {
            return {
                rowStart: isScheduledReport ? 0 : currentRowStartState,
                rowLimit: rowOverride,
            };
        }

        return {
            rowStart: currentRowStartState,
            rowLimit: PAGE_SIZE,
        };
    }, [isScheduledReport, isOverrideRowMode, currentRowStartState, rowOverride]);

    const params = useMemo(() => {
        const searchParam = currentSearchState ? { search: currentSearchState } : {};

        return {
            ...searchParam,
            rowStart,
            rowLimit,
        };
    }, [currentSearchState, rowStart, rowLimit]);

    const data = usePanelDataService(dataSource, params);

    const handlePreviousPage = () => {
        if (currentPage > 0) {
            rowStartStateActions.setValue(rowStartPageStateKey, currentRowStartState - PAGE_SIZE);
        }
    };

    const handleNextPage = () => {
        if (currentPage + 1 < totalPages) {
            rowStartStateActions.setValue(rowStartPageStateKey, currentRowStartState + PAGE_SIZE);
        }
    };

    const handleSearchChange = (value) => {
        if (isEmpty(value)) {
            searchStateActions.clearValue(searchPageStateKey);
        } else {
            searchStateActions.setValue(searchPageStateKey, value);
        }
    };

    useEffect(() => {
        const totalRows = data && data.total ? data.total : 0;
        const latestTotalPages = Math.ceil(divide(totalRows, rowLimit));
        const latestCurrentPage = Math.floor(divide(rowStart, rowLimit));
        setTotalPages(latestTotalPages);
        setCurrentPage(latestCurrentPage);
        setAllowPaging(totalRows > rowLimit);
        setAllowPrevious(latestCurrentPage > 0);
        setAllowNext(latestCurrentPage + 1 < latestTotalPages);
        setPagingText(
            totalRows > 0
                ? i18n._(
                      t`Showing ${rowStart + 1} - ${Math.min(totalRows, rowStart + rowLimit)} of ${totalRows} results`
                  )
                : ''
        );
    }, [data, rowStart, rowLimit]);

    useEffect(() => {
        rowStartStateActions.setValue(rowStartPageStateKey, 0);
    }, [currentSearchState, rowStartPageStateKey, rowStartStateActions]);

    return (
        <div className={clsx('flex-container', classes.performanceContainer)}>
            <div className={classes.toolbarContainer}>
                <div className="searchContainer">
                    <SearchContainer onSearchChange={handleSearchChange} defaultValue={currentSearchState} />
                </div>
                <div className="pagingContainer">
                    <div className="pagingText">{pagingText}</div>
                    {!isPrinting && allowPaging && (
                        <div className="pagingControlsContainer">
                            <IconButton
                                onClick={handlePreviousPage}
                                title={i18n._(translatedPrevious)}
                                className="pagingControls"
                                disabled={!allowPrevious}
                            >
                                <ArrowBackIosIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                onClick={handleNextPage}
                                title={i18n._(translatedNext)}
                                className="pagingControls"
                                disabled={!allowNext}
                            >
                                <ArrowForwardIosIcon fontSize="small" />
                            </IconButton>
                        </div>
                    )}
                </div>
            </div>
            <div className="scroll-container">
                <AgentTestList data={data} classes={classes} isTestPanel={isTestPanel} />
            </div>
        </div>
    );
}
